import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { contentAPI } from '../utils/api';
import { menuHighlights as fallbackData } from '../utils/mockData';

/**
 * Editorial bleed-edge menu highlights — alternating left/right large image
 * with serif title and description. No cards, no shadows.
 */
const MenuHighlights = () => {
  const navigate = useNavigate();
  const [items, setItems] = useState(fallbackData);

  useEffect(() => {
    contentAPI.getMenuHighlights().then((data) => {
      if (Array.isArray(data) && data.length > 0) setItems(data);
    });
  }, []);

  return (
    <section id="menu" className="py-20 md:py-28 bg-[#FFFDF9]" data-testid="menu-highlights-section">
      <div className="max-w-7xl mx-auto px-6">
        <div className="text-center mb-16 md:mb-24">
          <p
            className="text-[10px] md:text-xs tracking-[0.45em] uppercase text-[#C9A86C] mb-3"
            style={{ fontFamily: 'var(--font-serif)' }}
          >
            From the Kitchen
          </p>
          <h2
            className="text-4xl md:text-6xl mb-6"
            style={{ fontFamily: 'var(--font-serif)', fontWeight: 400 }}
          >
            Menu Highlights
          </h2>
          <p className="text-base md:text-lg text-gray-600 max-w-xl mx-auto leading-relaxed italic">
            A celebration of bold flavors, seasonal ingredients, and culinary
            traditions reimagined for the modern palate.
          </p>
        </div>

        <div className="space-y-20 md:space-y-28">
          {items
            .filter((i) => i.is_active !== false)
            .sort((a, b) => (a.order ?? 0) - (b.order ?? 0))
            .map((item, idx) => {
            const reverse = idx % 2 === 1;
            return (
              <div
                key={item.id || item._id || idx}
                className={`grid md:grid-cols-12 gap-8 md:gap-12 items-center ${reverse ? 'md:[direction:rtl]' : ''}`}
                data-testid={`menu-highlight-${item.id || idx}`}
              >
                <div className="md:col-span-7 md:[direction:ltr]">
                  <div className="aspect-[4/3] md:aspect-[5/4] overflow-hidden">
                    <img
                      src={item.image}
                      alt={item.name}
                      className="w-full h-full object-cover transition-transform duration-700 hover:scale-105"
                    />
                  </div>
                </div>
                <div className="md:col-span-5 md:[direction:ltr]">
                  <p className="text-[10px] md:text-xs tracking-[0.4em] uppercase text-[#C9A86C] mb-3">
                    {item.category || `0${idx + 1}`}
                  </p>
                  <h3
                    className="text-3xl md:text-4xl mb-5 leading-tight"
                    style={{ fontFamily: 'var(--font-serif)', fontWeight: 400 }}
                  >
                    {item.name}
                  </h3>
                  <p className="text-gray-600 leading-relaxed md:text-lg">
                    {item.description}
                  </p>
                </div>
              </div>
            );
          })}
        </div>

        <div className="text-center mt-20 md:mt-28">
          <button
            onClick={() => navigate('/menu')}
            className="text-sm tracking-[0.3em] uppercase border-b border-[#1a1a1a] pb-1 hover:opacity-60 transition-opacity"
            data-testid="menu-highlights-view-all"
          >
            View Full Menu →
          </button>
        </div>
      </div>
    </section>
  );
};

export default MenuHighlights;
